import type { ReactNode } from 'react';
import { LoadingSpinner } from '@/components/LoadingSpinner';
import { LockedFeatureCard } from '@/components/gating/LockedFeatureCard';
import { usePortfolioLocks } from '@/hooks/usePortfolioLocks';

interface PortfolioFeatureGateProps {
  /** Registry key, e.g. 'portfolio_timeline' */
  feature: string;
  /** Rendered when locked. Default: the locked teaser card. Pass `null` to render nothing. */
  fallback?: ReactNode;
  className?: string;
  children: ReactNode;
}

/**
 * FeatureGate for the Brand Portfolio: decides on the AGENCY package (`lockedFeatures` of the portfolio
 * response), never on the team-scoped entitlements. Nothing is locked unless the backend enforces.
 */
export const PortfolioFeatureGate = ({ feature, fallback, className, children }: PortfolioFeatureGateProps) => {
  const { isLocked, decided } = usePortfolioLocks();
  // Undecided (portfolio response still loading): neither the view nor the locked card, so nothing mounts or fetches early
  if (!decided) {
    return (
      <div className="px-4 sm:px-6 py-10">
        <LoadingSpinner />
      </div>
    );
  }
  if (!isLocked(feature)) return <>{children}</>;
  if (fallback !== undefined) return <>{fallback}</>;
  return (
    <div className={className ?? 'px-4 sm:px-6 py-10'}>
      <LockedFeatureCard feature={feature} className="max-w-xl mx-auto" />
    </div>
  );
};

export default PortfolioFeatureGate;
